/**
 * DismissibleBanner Component
 *
 * Purpose: A dismissible alert/banner component with close functionality
 * Features: Close button, optional localStorage persistence, smooth collapse animation
 *
 * Used in:
 * - MDMManagerPage.tsx
 * - MetadataManagerPage.tsx
 * - TenantManagerPage.tsx
 */

import React, { useState, useCallback } from 'react';
import { Alert, AlertProps, Collapse, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { GlobalWarningBanner } from './StyledComponents';

export interface DismissibleBannerProps extends Omit<AlertProps, 'onClose'> {
  /** Unique key for localStorage persistence (required when persistDismissal is true) */
  storageKey?: string;
  /** Whether to persist dismissal across page refreshes (default: false) */
  persistDismissal?: boolean;
  /** Whether to use GlobalWarningBanner styling instead of default Alert */
  useGlobalStyle?: boolean;
  /** Callback when banner is dismissed */
  onDismiss?: () => void;
  /** Children content to display in the banner */
  children: React.ReactNode;
}

/**
 * DismissibleBanner - Alert banner with close button
 *
 * @param storageKey - Required when persistDismissal is true, used as localStorage key
 * @param persistDismissal - If true, dismissal persists across page refreshes (default: false)
 * @param useGlobalStyle - Use GlobalWarningBanner styled component
 * @param onDismiss - Callback fired when banner is dismissed
 * @param children - Banner content
 * @param ...alertProps - All other MUI Alert props (severity, icon, sx, etc.)
 */
export const DismissibleBanner: React.FC<DismissibleBannerProps> = ({
  storageKey,
  persistDismissal = false,
  useGlobalStyle = false,
  onDismiss,
  children,
  ...alertProps
}) => {
  // Check if banner was previously dismissed (only if persistence is enabled)
  const getInitialState = useCallback(() => {
    if (persistDismissal && storageKey) {
      return localStorage.getItem(`banner_dismissed_${storageKey}`) !== 'true';
    }
    return true;
  }, [persistDismissal, storageKey]);

  const [visible, setVisible] = useState(getInitialState);

  const handleClose = useCallback(() => {
    setVisible(false);
    if (persistDismissal && storageKey) {
      localStorage.setItem(`banner_dismissed_${storageKey}`, 'true');
    }
    onDismiss?.();
  }, [persistDismissal, storageKey, onDismiss]);

  const AlertComponent = useGlobalStyle ? GlobalWarningBanner : Alert;

  return (
    <Collapse in={visible} unmountOnExit>
      <AlertComponent
        {...alertProps}
        action={
          <IconButton
            aria-label="close"
            color="inherit"
            size="small"
            onClick={handleClose}
            sx={{ mt: -0.5 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      >
        {children}
      </AlertComponent>
    </Collapse>
  );
};

export default DismissibleBanner;
